// Single source of truth for the desktop/mobile split. MUST match the CSS
// breakpoint (@media max-width: 767px in section-storytelling-module.css) —
// the JS previously keyed off innerWidth < 990, so a band of widths rendered the
// mobile pill carousel in CSS while the JS ran desktop scroll logic and never
// positioned the pills. matchMedia also tracks the viewport exactly as the CSS
// does (innerWidth includes the scrollbar; the media query does not). AAS-4782
// Split moved 1024px → 767px (theme's 768 family) per AAS-4633 review.
const MOBILE_MQ = window.matchMedia('(max-width: 767px)');

class StorytellingModule extends HTMLElement {
  constructor() {
    super();
    this._onScroll = this._onScroll.bind(this);
    this._update = this._update.bind(this);
    this._onResize = this._onResize.bind(this);
    this._ticking = false;
    this._isIntersecting = false;
    this.activeIndex = 0;
  }

  connectedCallback() {
    this.scrollContainer = this.querySelector('.stm__scroll-container');
    this.slides = [...this.querySelectorAll('.stm__slide')];
    this.mediaLayers = [...this.querySelectorAll('.stm__media-layer')];

    if (!this.scrollContainer || !this.slides.length) return;

    // Remembered so _onResize can tell when the viewport crosses the boundary.
    this._isMobile = MOBILE_MQ.matches;

    this._setupVideoTracking();
    this._fitPillWidth();
    this._positionSlides();
    this._setActiveSlide(0);

    // Headline metrics change when the web font lands — re-fit then, and
    // reposition the mobile carousel (its translateX math reads offsetWidth).
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        this._fitPillWidth();
        if (MOBILE_MQ.matches) this._positionSlidePills(this.activeIndex);
      });
    }

    window.addEventListener('resize', this._onResize, { passive: true });

    this._observer = new IntersectionObserver(
      ([entry]) => { this._isIntersecting = entry.isIntersecting; },
      { rootMargin: '100px' }
    );
    this._observer.observe(this.scrollContainer);

    window.addEventListener('scroll', this._onScroll, { passive: true });

    this.slides.forEach((slide, i) => {
      slide.addEventListener('click', () => this._jumpToSlide(i));
      slide.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this._jumpToSlide(i);
        }
      });
    });

    this.querySelectorAll('.stm__slide-pause-btn').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        this._togglePause();
      });
    });

    this._mobileTimer = this.querySelector('.stm__mobile-timer');
    this._setupSwipe();
    this._setupVideoModal();

    this._update();
  }

  /* Fullscreen video CTA (AAS-4668). Mirrors the Play modal in
     section-video-with-text-overlay.js: a Shopify-hosted <video> plays directly,
     a YouTube/Vimeo embed has its src built on open so nothing is requested from
     the provider until the visitor asks to play. Closes on the button, a click
     on the backdrop, or Escape. All of it is inert when the section has no video
     — Liquid omits both the trigger and the modal. */
  _setupVideoModal() {
    this._videoModal = this.querySelector('.stm__video-modal');
    this._videoOpenBtn = this.querySelector('.stm__video-open');
    if (!this._videoModal || !this._videoOpenBtn) return;

    this._videoPlayer = this._videoModal.querySelector('.stm__video-player');
    this._videoIframe = this._videoModal.querySelector('.stm__video-iframe');
    this._videoCloseBtn = this._videoModal.querySelector('.stm__video-close');

    /* Move the modal to <body> so it escapes the section's stacking context.
       It is position:fixed at z-index 1000 — above the header's 900 — but a
       z-index only competes inside its own stacking context, and sections sit
       inside one, so left in place the nav paints over the player and its shim.
       Re-parenting is the same fix pdp-gallery-apparel-overlay.js uses for its
       backdrop. Because the modal is no longer a descendant of this element,
       the open state is a class on the modal itself, not on the host. */
    document.body.appendChild(this._videoModal);

    this._videoOpenBtn.addEventListener('click', () => this._openVideo());
    this._videoCloseBtn?.addEventListener('click', () => this._closeVideo());

    // Backdrop only — a click on the player itself must not dismiss it.
    this._videoModal.addEventListener('click', (e) => {
      if (e.target === this._videoModal) this._closeVideo();
    });

    this._videoKeyHandler = (e) => {
      if (e.key === 'Escape' && this._videoModal.classList.contains('is-open')) this._closeVideo();
    };
    document.addEventListener('keydown', this._videoKeyHandler);
  }

  _openVideo() {
    if (!this._videoModal) return;

    if (this._videoIframe && !this._videoIframe.src) {
      const id = this.dataset.videoId;
      const type = this.dataset.videoType;
      if (!id) return;
      this._videoIframe.src =
        type === 'youtube'
          ? `https://www.youtube.com/embed/${id}?autoplay=1&rel=0&modestbranding=1`
          : `https://player.vimeo.com/video/${id}?autoplay=1`;
    }

    this._videoModal.classList.add('is-open');
    this._videoModal.setAttribute('aria-hidden', 'false');
    window.ScrollLock ? ScrollLock.lock('storytelling') : (document.body.style.overflow = 'hidden');

    if (this._videoPlayer) this._videoPlayer.play().catch(() => {});
    this._videoCloseBtn?.focus();
  }

  _closeVideo() {
    if (!this._videoModal) return;

    this._videoModal.classList.remove('is-open');
    this._videoModal.setAttribute('aria-hidden', 'true');
    window.ScrollLock ? ScrollLock.unlock('storytelling') : (document.body.style.overflow = '');

    // Stop playback on close — pause the hosted video, and drop the iframe src
    // so an embedded player can't keep running (and its audio) behind the page.
    if (this._videoPlayer) this._videoPlayer.pause();
    if (this._videoIframe) this._videoIframe.src = '';

    this._videoOpenBtn?.focus();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this._onScroll);
    window.removeEventListener('resize', this._onResize);
    if (this._videoKeyHandler) document.removeEventListener('keydown', this._videoKeyHandler);
    // The modal lives on <body>, so it must be removed explicitly — otherwise a
    // theme-editor re-render leaves an orphan behind on every reload.
    this._videoModal?.remove();
    this._observer?.disconnect();
    this._mediaObservers?.forEach((obs) => obs.disconnect());
  }

  _onScroll() {
    if (!this._isIntersecting || this._ticking) return;
    window.requestAnimationFrame(this._update);
    this._ticking = true;
  }

  _update() {
    this._ticking = false;
    if (!this.scrollContainer) return;
    if (MOBILE_MQ.matches) return;

    const rect = this.scrollContainer.getBoundingClientRect();
    const count = this.slides.length;
    const scrollHeight = this.scrollContainer.offsetHeight - window.innerHeight;

    if (scrollHeight <= 0) return;

    let progress = -rect.top / scrollHeight;
    progress = Math.max(0, Math.min(1, progress));

    const newIndex = Math.min(Math.floor(progress * count), count - 1);

    if (newIndex !== this.activeIndex) {
      this._setActiveSlide(newIndex);
    }
  }

  _setActiveSlide(index) {
    const prevIndex = this.activeIndex;

    this.slides.forEach((slide, i) => {
      const isActive = i === index;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    this.mediaLayers.forEach((layer, i) => {
      const isActive = i === index;
      layer.classList.toggle('is-active', isActive);
      layer.setAttribute('aria-hidden', isActive ? 'false' : 'true');

      if (i < index) {
        layer.dataset.position = 'before';
      } else if (i > index) {
        layer.dataset.position = 'after';
      } else {
        delete layer.dataset.position;
      }
      layer.style.transform = '';
    });

    this._manageVideos(index, prevIndex);
    this.activeIndex = index;

    if (MOBILE_MQ.matches) {
      this._animatePillTransition(index, prevIndex);
    }
  }

  // AAS-4633: pill sizing. The design widths (188px desktop / 230px mobile)
  // clipped long localized headlines (de "Belag & Kanten präparieren" ran under
  // the number). Two behaviours by breakpoint:
  //   desktop — one SHARED width fitted to the longest nowrap headline
  //     (--stm-pill-width on the host; chrome: 11 left pad + 20 min gap +
  //     12 number + 10 right pad). Only set when it exceeds the 188px design
  //     width, so short locales keep the exact Figma size.
  //   mobile — each pill takes its OWN natural width (headline + 15px pads +
  //     30px active number + 10px gap), capped so the centred pill can never
  //     reach the ±50px peek zones of its neighbours. Collapsed pills stay the
  //     one-line 48px row with the overflow masked (CSS); only the EXPANDED
  //     card reveals the full headline, wrapping at the cap — the expand
  //     animation's scrollHeight measurement picks the extra line up for free.
  _fitPillWidth() {
    const heads = this.slides.map((s) => s.querySelector('.stm__slide-headline'));

    if (MOBILE_MQ.matches) {
      this.style.removeProperty('--stm-pill-width');

      // Natural (unwrapped) widths in one batch — the expanded pill's headline
      // wraps (white-space: normal), so force nowrap during measurement.
      heads.forEach((h) => { if (h) h.style.whiteSpace = 'nowrap'; });
      const naturals = heads.map((h) => (h ? h.offsetWidth : 0));
      heads.forEach((h) => { if (h) h.style.whiteSpace = ''; });

      // Cap: the centred pill must stay clear of the ±50px neighbour peek
      // zones. Floor: 300px design minimum (short headlines otherwise gave
      // cramped boxes) — the cap wins on narrow phones where 300 doesn't fit.
      const cap = Math.min(320, window.innerWidth - 110);
      const minW = Math.min(300, cap);
      this.slides.forEach((slide, i) => {
        slide.style.width = `${Math.min(cap, Math.max(minW, Math.ceil(naturals[i] + 70)))}px`;
      });
      return;
    }

    // Desktop — clear any mobile per-pill sizing, fit the shared width.
    let maxHeadline = 0;
    this.slides.forEach((slide, i) => {
      slide.style.width = '';
      if (heads[i]) maxHeadline = Math.max(maxHeadline, heads[i].offsetWidth);
    });
    if (!maxHeadline) return;

    const needed = Math.ceil(maxHeadline + 53);
    const next = needed > 188 ? `${needed}px` : '';
    if (next === this.style.getPropertyValue('--stm-pill-width')) return;

    // A refit is a correction (init, web-font landing, resize), not a state
    // change — snap it. Letting the pill's 0.5s width transition animate it
    // made every pill glide and the numbers visibly jump on load. AAS-4633
    this.slides.forEach((s) => { s.style.transition = 'none'; });
    if (next) {
      this.style.setProperty('--stm-pill-width', next);
    } else {
      this.style.removeProperty('--stm-pill-width');
    }
    requestAnimationFrame(() => requestAnimationFrame(() => {
      this.slides.forEach((s) => { s.style.transition = ''; });
    }));
  }

  _positionSlides() {
    this._slidesEl = this.querySelector('.stm__slides');
    if (!this._slidesEl) return;

    const height = this._slidesEl.offsetHeight;
    this._slidesEl.style.top = `${(window.innerHeight - height) / 2}px`;
    this._slidesEl.classList.add('is-positioned');
  }

  _onResize() {
    if (this._resizeTimer) clearTimeout(this._resizeTimer);
    this._resizeTimer = setTimeout(() => {
      const isMobile = MOBILE_MQ.matches;
      const modeChanged = isMobile !== this._isMobile;
      this._isMobile = isMobile;

      // Crossing the desktop/mobile boundary leaves the previous layout's inline
      // styles on the slides — the mobile pill path writes transform / maxHeight
      // / transitionDelay + .is-expanded, none of which the desktop layout wants
      // (and vice-versa). Clear them so the new layout starts from its CSS base;
      // without this the slides stay scattered until a page reload. AAS-4782
      if (modeChanged) this._resetLayoutStyles();

      // Re-fit the shared pill width — the breakpoint changes the floor and
      // chrome (188/desktop vs 230/mobile), and font metrics can differ. AAS-4633
      this._fitPillWidth();

      this._positionSlides();

      if (isMobile) {
        this._positionSlidePills(this.activeIndex);
        this._expandSlide(this.slides[this.activeIndex]);
      } else if (modeChanged) {
        // Re-derive the active slide from the scroll position on desktop.
        this._update();
      }
    }, 150);
  }

  // Strip the inline styles/classes each layout writes onto the slides and media
  // layers, so switching modes doesn't inherit stale positioning. AAS-4782
  _resetLayoutStyles() {
    // Cancel any in-flight mobile pill animation so its deferred callbacks can't
    // re-apply mobile transforms after we've switched to desktop.
    clearTimeout(this._moveTimer);
    clearTimeout(this._expandTimer);
    clearTimeout(this._collapseTimer);

    this.slides.forEach((slide) => {
      slide.style.transform = '';
      slide.style.maxHeight = '';
      slide.style.transitionDelay = '';
      slide.style.width = '';
      slide.classList.remove('is-expanded');
    });
    this.mediaLayers.forEach((layer) => {
      layer.style.transform = '';
      layer.style.transition = '';
    });
  }

  _jumpToSlide(index) {
    if (index === this.activeIndex) return;

    if (MOBILE_MQ.matches) {
      this._setActiveSlide(index);
      return;
    }

    const count = this.slides.length;
    const scrollHeight = this.scrollContainer.offsetHeight - window.innerHeight;
    const rect = this.scrollContainer.getBoundingClientRect();
    const containerTop = window.scrollY + rect.top;
    const targetProgress = (index + 0.5) / count;
    const targetScrollTop = containerTop + targetProgress * scrollHeight;

    window.scrollTo({ top: targetScrollTop, behavior: 'smooth' });
  }

  _setupVideoTracking() {
    this._videos = new Map();
    this._mediaObservers = [];

    this.mediaLayers.forEach((layer, i) => {
      const video = layer.querySelector('video');
      if (video) {
        this._videos.set(i, video);
        this._bindVideo(i, video);
        if (i !== 0) video.pause();
      } else {
        const observer = new MutationObserver(() => {
          const v = layer.querySelector('video');
          if (v) {
            this._videos.set(i, v);
            this._bindVideo(i, v);
            if (i === this.activeIndex) {
              v.play().catch(() => {});
            } else {
              v.pause();
            }
            observer.disconnect();
          }
        });
        observer.observe(layer, { childList: true, subtree: true });
        this._mediaObservers.push(observer);
      }
    });
  }

  _bindVideo(index, video) {
    video.addEventListener('timeupdate', () => {
      if (index === this.activeIndex) this._updateTime(video, index);
    });
    video.addEventListener('loadedmetadata', () => {
      if (index === this.activeIndex) this._updateTime(video, index);
    });
  }

  _manageVideos(activeIndex, prevIndex) {
    if (!this._videos) return;

    const prevVideo = this._videos.get(prevIndex);
    if (prevVideo) prevVideo.pause();

    const activeVideo = this._videos.get(activeIndex);
    if (activeVideo) {
      activeVideo.play().catch(() => {});
      this._updateTime(activeVideo, activeIndex);
      this._setPauseState(false);
    }

    if (this._mobileTimer) {
      this._mobileTimer.classList.toggle('is-hidden', !activeVideo);
    }
  }

  _togglePause() {
    const video = this._videos.get(this.activeIndex);
    if (!video) return;

    if (video.paused) {
      video.play().catch(() => {});
      this._setPauseState(false);
    } else {
      video.pause();
      this._setPauseState(true);
    }
  }

  _setPauseState(isPaused) {
    const activeSlide = this.slides[this.activeIndex];
    const btn = activeSlide?.querySelector('.stm__slide-pause-btn');
    if (btn) {
      btn.classList.toggle('is-paused', isPaused);
      btn.setAttribute(
        'aria-label',
        isPaused ? btn.dataset.playLabel : btn.dataset.pauseLabel
      );
    }

    const mobileBtn = this._mobileTimer?.querySelector('.stm__mobile-pause-btn');
    if (mobileBtn) {
      mobileBtn.classList.toggle('is-paused', isPaused);
      mobileBtn.setAttribute(
        'aria-label',
        isPaused ? mobileBtn.dataset.playLabel : mobileBtn.dataset.pauseLabel
      );
    }
  }

  _updateTime(video, index) {
    if (!video || !video.duration) return;

    const current = this._fmt(video.currentTime);
    const total = this._fmt(video.duration);
    const text = `${current} / ${total}`;

    const slide = this.slides[index];
    const timeEl = slide?.querySelector('.stm__slide-time');
    if (timeEl) timeEl.textContent = text;

    const mobileTimeEl = this._mobileTimer?.querySelector('.stm__mobile-time');
    if (mobileTimeEl) mobileTimeEl.textContent = text;
  }

  _setupSwipe() {
    const target = this.querySelector('.stm__sticky');
    if (!target) return;

    let startX = 0;
    let startY = 0;
    let tracking = false;
    let swiping = false;

    target.addEventListener('touchstart', (e) => {
      if (!MOBILE_MQ.matches) return;
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      tracking = true;
      swiping = false;
    }, { passive: true });

    target.addEventListener('touchmove', (e) => {
      if (!tracking) return;
      const dx = e.touches[0].clientX - startX;
      const dy = e.touches[0].clientY - startY;

      if (!swiping && Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 10) {
        swiping = true;
        this.mediaLayers.forEach(layer => { layer.style.transition = 'none'; });
      }

      if (swiping) {
        e.preventDefault();
        this._dragLayers(dx / target.offsetWidth);
      }
    }, { passive: false });

    target.addEventListener('touchend', (e) => {
      if (!tracking) return;
      tracking = false;
      if (!swiping) return;
      swiping = false;

      this.mediaLayers.forEach(layer => { layer.style.transition = ''; });

      const dx = e.changedTouches[0].clientX - startX;
      const threshold = target.offsetWidth * 0.2;
      const count = this.slides.length;

      if (dx < -threshold && this.activeIndex < count - 1) {
        this._setActiveSlide(this.activeIndex + 1);
      } else if (dx > threshold && this.activeIndex > 0) {
        this._setActiveSlide(this.activeIndex - 1);
      } else {
        this.mediaLayers.forEach(layer => { layer.style.transform = ''; });
      }
    }, { passive: true });
  }

  _dragLayers(progress) {
    const current = this.mediaLayers[this.activeIndex];
    if (!current) return;

    if (progress < 0 && this.activeIndex < this.mediaLayers.length - 1) {
      const next = this.mediaLayers[this.activeIndex + 1];
      current.style.transform = `translateX(${progress * 100}%)`;
      next.style.transform = `translateX(${(1 + progress) * 100}%)`;
    } else if (progress > 0 && this.activeIndex > 0) {
      const prev = this.mediaLayers[this.activeIndex - 1];
      current.style.transform = `translateX(${progress * 100}%)`;
      prev.style.transform = `translateX(${(-1 + progress) * 100}%)`;
    } else {
      current.style.transform = `translateX(${progress * 30}%)`;
    }
  }

  _expandSlide(slide) {
    if (!slide) return;
    slide.style.maxHeight = '48px';
    slide.classList.add('is-expanded');
    void slide.offsetHeight;
    slide.style.maxHeight = `${slide.scrollHeight}px`;
  }

  _collapseSlide(slide) {
    if (!slide) return;
    slide.style.maxHeight = '48px';
    if (this._collapseTimer) clearTimeout(this._collapseTimer);
    this._collapseTimer = setTimeout(() => {
      slide.classList.remove('is-expanded');
    }, 350);
  }

  _animatePillTransition(index, prevIndex) {
    if (this._expandTimer) clearTimeout(this._expandTimer);
    if (this._moveTimer) clearTimeout(this._moveTimer);

    if (index === prevIndex) {
      this._positionSlidePills(index);
      this._expandSlide(this.slides[index]);
      return;
    }

    this._collapseSlide(this.slides[prevIndex]);

    this._moveTimer = setTimeout(() => {
      this._positionSlidePills(index, prevIndex);

      this._expandTimer = setTimeout(() => {
        this._expandSlide(this.slides[index]);
      }, 600);
    }, 350);
  }

  _positionSlidePills(activeIndex, prevIndex) {
    if (!this._slidesEl) return;
    const containerW = this._slidesEl.offsetWidth;
    const peek = 50;
    const direction = prevIndex !== undefined ? activeIndex - prevIndex : 0;

    this.slides.forEach((slide, i) => {
      const w = slide.offsetWidth;
      const diff = i - activeIndex;

      let tx;
      if (diff === 0) {
        tx = (containerW - w) / 2;
      } else if (diff === 1) {
        tx = containerW - peek;
      } else if (diff === -1) {
        tx = -w + peek;
      } else if (diff > 1) {
        tx = containerW + 20;
      } else {
        tx = -w - 20;
      }

      let delay = 0;
      if (direction !== 0) {
        if (i === prevIndex) delay = 0;
        else if (diff === 0) delay = 0.1;
        else if ((direction > 0 && diff === 1) || (direction < 0 && diff === -1)) delay = 0.2;
      }

      slide.style.transitionDelay = `${delay}s`;
      slide.style.transform = `translateX(${tx}px) translateY(-50%)`;
    });
  }

  _fmt(seconds) {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  }
}

customElements.define('storytelling-module', StorytellingModule);
